import { removeBlock } from '@gitbook/doc-core';
import { createPalette, type PaletteDef, type PaletteGroupDef } from '@gitbook/react-uikit';
import { BlockRange, isBlockRangeSelection } from '@gitbook/slate';
import { useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { useCompletionApi } from '../completion';
import { useBlockIfPaletteGroup } from '../if';
import { useReusableContentPaletteGroup } from '../reusable-content';

/**
 * Create a group for block actions.
 */
function useActionsGroupFactory(input: { nodeKey: string }): () => PaletteGroupDef {
    const { nodeKey } = input;
    const editor = useEditor();
    return useEventCallback(() => {
        return {
            key: 'block',
            commands: [
                {
                    key: 'duplicate',
                    title: 'Duplicate',
                    icon: Icon.Duplicate,
                    trigger: () => {
                        editor.change((change) => {
                            change.duplicateBlocks();
                        });
                    },
                },
                {
                    key: 'delete',
                    title: 'Delete',
                    icon: Icon.Trashcan,
                    trigger: () => {
                        editor.change((change) => {
                            if (isBlockRangeSelection(change.value.selection)) {
                                change.deleteBlockRangeForward();
                            } else {
                                removeBlock(change, nodeKey).focus();
                            }
                        });
                    },
                },
            ],
        };
    });
}

/**
 * Create a group for AI completion.
 */
function useAIPaletteGroupFactory(input: { nodeKey: string }): () => PaletteGroupDef | null {
    const { nodeKey } = input;
    const editor = useEditor();
    const completionApi = useCompletionApi();
    return useEventCallback(() => {
        if (!completionApi) {
            return null;
        }
        return {
            key: 'ai',
            commands: () => {
                return [
                    {
                        key: 'ask-ai',
                        title: 'Ask AI',
                        icon: Icon.Magic,
                        trigger: () => {
                            if (!isBlockRangeSelection(editor.value.selection)) {
                                const block = editor.value.document.assertBlock(nodeKey);
                                const selection = BlockRange.create({
                                    anchorBlock: block.key,
                                    focusBlock: block.key,
                                    isFocused: false,
                                });
                                editor.change((change) => {
                                    change.selectBlock(selection);
                                });
                            }
                            // Wait for the selection to be updated
                            setTimeout(() => {
                                completionApi.open({ trigger: 'block-options' });
                            });
                        },
                    },
                ];
            },
        };
    });
}

/**
 * Create a palette factory for a block.
 */
export function useBlockPaletteFactory(input: {
    nodeKey: string;
    createExtraGroups?: () => PaletteGroupDef[];
    aiCompletion?: boolean;
    /*
     * Set to false if the reusable content group is already added in a different group.
     */
    addReusableContentGroup?: boolean;
    /*
     * Set to false if the conditional content group is already added in a different group.
     */
    addConditionalContentGroup?: boolean;
    search?: PaletteDef['search'];
    position?: PaletteDef['position'];
}): () => PaletteDef {
    const {
        createExtraGroups,
        aiCompletion,
        nodeKey,
        addReusableContentGroup = true,
        addConditionalContentGroup = true,
        position,
        search,
    } = input;
    const createReusableContentBlockGroup = useReusableContentPaletteGroup({
        nodeKey,
        method: 'editor-palette',
    });
    const createConditionalContentBlockGroup = useBlockIfPaletteGroup({ nodeKey });
    const createAIGroup = useAIPaletteGroupFactory({ nodeKey });
    const createActionsGroup = useActionsGroupFactory({ nodeKey });
    return useEventCallback(() => {
        const aiCompletionGroup = aiCompletion ? createAIGroup() : null;
        const reusableContentBlockGroup = addReusableContentGroup
            ? createReusableContentBlockGroup()
            : null;
        const conditionalContentGroup = addConditionalContentGroup
            ? createConditionalContentBlockGroup()
            : null;
        const extraGroups = createExtraGroups ? createExtraGroups() : [];
        const actionsGroup = createActionsGroup();
        const groups = [
            aiCompletionGroup,
            reusableContentBlockGroup,
            conditionalContentGroup,
            ...extraGroups,
            actionsGroup,
        ].filter(filterOutNullable);

        return createPalette({
            groups,
            search,
            position,
        });
    });
}
